/**
 * 票链云 — 共享工具函数
 *
 * 只放**前后端都需要且必须行为一致**的函数。特别是金额换算与脱敏：
 * 如果前后端各写一套，迟早会出现「前端显示的和后端存的差一分钱」。
 */
import { RISK_LEVEL_WEIGHT } from './enums.js';
/* ==========================================================================
 * 金额
 *
 * 全系统金额一律以「分」为单位存整数，避免浮点累加误差。
 * 只有在展示与用户输入时才转「元」。
 * ========================================================================== */
/** 元 → 分。入参可为字符串（用户输入）或数字（接口返回）。 */
export function yuanToCents(input) {
    if (input === null || input === undefined || input === '')
        return null;
    const raw = typeof input === 'number' ? input : Number(String(input).replace(/[¥￥,\s]/g, ''));
    if (!Number.isFinite(raw))
        return null;
    // 先转字符串再算，规避 0.1*100 = 10.000000000000002 这类误差
    return Math.round(raw * 100);
}
/** 分 → 元，返回数字 */
export function centsToYuan(cents) {
    if (cents === null || cents === undefined)
        return null;
    return cents / 100;
}
/** 分 → 元的展示字符串，保留两位小数 */
export function formatCents(cents) {
    if (cents === null || cents === undefined)
        return '-';
    return (cents / 100).toFixed(2);
}
/** 分 → 带千分位的金额展示，如 ¥54,000.00 */
export function formatMoney(cents) {
    if (cents === null || cents === undefined)
        return '-';
    const yuan = cents / 100;
    return `¥${yuan.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
/** 分 → 中文大写金额，用于与票面「价税合计（大写）」核对 */
export function centsToChinese(cents) {
    if (cents === null || cents === undefined)
        return '';
    if (cents === 0)
        return '零圆整';
    const digits = ['零', '壹', '贰', '叁', '肆', '伍', '陆', '柒', '捌', '玖'];
    const intUnits = ['', '拾', '佰', '仟', '万', '拾', '佰', '仟', '亿', '拾', '佰', '仟'];
    const decUnits = ['角', '分'];
    const negative = cents < 0;
    let abs = Math.abs(cents);
    const yuanPart = Math.floor(abs / 100);
    const jiaoPart = Math.floor((abs % 100) / 10);
    const fenPart = abs % 10;
    let result = '';
    if (yuanPart > 0) {
        const str = String(yuanPart);
        let zeroPending = false;
        for (let i = 0; i < str.length; i++) {
            const d = Number(str[i]);
            const unitIndex = str.length - 1 - i;
            if (d === 0) {
                zeroPending = true;
                // 万/亿节末尾需要补单位
                if (unitIndex === 4 || unitIndex === 8) {
                    // 去掉可能重复的节单位
                    if (!result.endsWith(intUnits[unitIndex])) {
                        result += intUnits[unitIndex];
                    }
                }
            }
            else {
                if (zeroPending && result !== '')
                    result += digits[0];
                zeroPending = false;
                result += digits[d] + intUnits[unitIndex];
            }
        }
        result += '圆';
    }
    if (jiaoPart === 0 && fenPart === 0) {
        result += '整';
    }
    else {
        if (jiaoPart > 0) {
            result += digits[jiaoPart] + decUnits[0];
        }
        else if (yuanPart > 0) {
            result += digits[0];
        }
        if (fenPart > 0) {
            result += digits[fenPart] + decUnits[1];
        }
    }
    return (negative ? '负' : '') + result;
}
/* ==========================================================================
 * 脱敏（文档 4.2 字段权限、8.1 敏感字段）
 * ========================================================================== */
/**
 * 统一社会信用代码 / 税号脱敏。
 * 保留头 4 位与尾 4 位，中间固定 6 个星号（不暴露原长度，减少信息量）。
 */
export function maskTaxNo(value) {
    if (!value)
        return '';
    const s = String(value).trim();
    if (s.length <= 8)
        return '*'.repeat(s.length);
    return `${s.slice(0, 4)}******${s.slice(-4)}`;
}
/** 银行账号脱敏：仅保留后 4 位 */
export function maskBankAccount(value) {
    if (!value)
        return '';
    const s = String(value).trim();
    if (s.length <= 4)
        return '*'.repeat(s.length);
    return `${'*'.repeat(Math.min(s.length - 4, 12))}${s.slice(-4)}`;
}
/** 手机号脱敏：138****8000 */
export function maskPhone(value) {
    if (!value)
        return '';
    const s = String(value).trim();
    if (s.length < 7)
        return '*'.repeat(s.length);
    return `${s.slice(0, 3)}****${s.slice(-4)}`;
}
/** 身份证号脱敏：保留头 3 尾 2 */
export function maskIdCard(value) {
    if (!value)
        return '';
    const s = String(value).trim();
    if (s.length <= 5)
        return '*'.repeat(s.length);
    return `${s.slice(0, 3)}${'*'.repeat(s.length - 5)}${s.slice(-2)}`;
}
/** 地址脱敏：只保留到区县级，其余打码 */
export function maskAddress(value) {
    if (!value)
        return '';
    const s = String(value).trim();
    if (s.length <= 6)
        return s;
    return `${s.slice(0, 6)}${'*'.repeat(Math.min(s.length - 6, 10))}`;
}
/**
 * 按策略脱敏任意值。
 * 用于 FieldPolicy 的统一执行入口。
 */
export function applyMask(value, rule, keepHead = 4, keepTail = 4) {
    if (value === null || value === undefined)
        return '';
    const s = String(value);
    if (rule === 'FULL')
        return '*'.repeat(Math.min(s.length, 8));
    if (s.length <= keepHead + keepTail)
        return '*'.repeat(s.length);
    return `${s.slice(0, keepHead)}${'*'.repeat(6)}${s.slice(-keepTail)}`;
}
/** 日志用：截断过长字符串，避免把原始报文整个打进日志 */
export function truncate(value, max = 500) {
    if (value === null || value === undefined)
        return '';
    const s = typeof value === 'string' ? value : JSON.stringify(value);
    return s.length > max ? `${s.slice(0, max)}...(${s.length} chars)` : s;
}
/* ==========================================================================
 * 风险等级
 * ========================================================================== */
/** 取两个风险等级中较高的那个 */
export function maxRiskLevel(a, b) {
    return RISK_LEVEL_WEIGHT[a] >= RISK_LEVEL_WEIGHT[b] ? a : b;
}
/** a 是否比 b 风险更高 */
export function isRiskHigherThan(a, b) {
    return RISK_LEVEL_WEIGHT[a] > RISK_LEVEL_WEIGHT[b];
}
/** 判断某风险等级是否在允许放行范围内 */
export function isRiskAllowed(level, maxAllowed) {
    if (maxAllowed === null || maxAllowed === undefined)
        return true;
    return RISK_LEVEL_WEIGHT[level] <= RISK_LEVEL_WEIGHT[maxAllowed];
}
/* ==========================================================================
 * 业务编号
 * ========================================================================== */
/** 生成日期前缀，如 20260913 */
export function datePrefix(date = new Date()) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}${m}${d}`;
}
/** 生成随机数字串（用于编号后缀） */
export function randomDigits(length) {
    let out = '';
    for (let i = 0; i < length; i++) {
        out += Math.floor(Math.random() * 10);
    }
    return out;
}
/** 日期 + 随机数的业务编号，如 BZ20260913001234 */
export function generateBizNo(prefix = 'BZ', date = new Date()) {
    return `${prefix}${datePrefix(date)}${randomDigits(6)}`;
}
/** 校验报告编号，如 RPT20260913001234 */
export function generateReportNo(date = new Date()) {
    return generateBizNo('RPT', date);
}
/* —— 合同与费用的单据编号 ——
 *
 * 统一走 `generateBizNo`（前缀 + 日期 + 6 位随机），与既有的发票/报告编号同款：
 * 前缀区分单据种类，日期让人一眼看出新旧，随机段避免同一天批量创建时撞号。
 * 各单据表上另有 `UNIQUE (tenant_id, xxx_no)`，撞号会被数据库挡住而不是静默覆盖。
 */
/** 合同编号，如 HT20260923001234 */
export function generateContractNo(date = new Date()) {
    return generateBizNo('HT', date);
}
/** 合同变更/补充协议编号，如 BG20260923001234 */
export function generateContractChangeNo(date = new Date()) {
    return generateBizNo('BG', date);
}
/** 合同付款单号，如 FK20260923001234 */
export function generateContractPaymentNo(date = new Date()) {
    return generateBizNo('FK', date);
}
/** 费用申请单号，如 SQ20260923001234 */
export function generateExpenseApplyNo(date = new Date()) {
    return generateBizNo('SQ', date);
}
/** 报销单号，如 BX20260923001234 */
export function generateExpenseReimburseNo(date = new Date()) {
    return generateBizNo('BX', date);
}
/** 报销付款单号，如 FK20260923001234（与合同付款共用 FK 前缀，单号各自唯一） */
export function generateExpensePaymentNo(date = new Date()) {
    return generateBizNo('FK', date);
}
/* ==========================================================================
 * 日期
 * ========================================================================== */
/** Date → YYYYMMDD（税务查验格式） */
export function toTaxDate(date) {
    return datePrefix(date);
}
/** YYYYMMDD → YYYY-MM-DD（展示格式） */
export function formatTaxDate(taxDate) {
    if (!taxDate)
        return '';
    const s = String(taxDate).replace(/\D/g, '');
    if (s.length !== 8)
        return String(taxDate);
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
}
/** 是否超过给定小时数（用于流程节点超时判定） */
export function isOverdue(fromIso, hours, now = new Date()) {
    const from = new Date(fromIso).getTime();
    if (!Number.isFinite(from))
        return false;
    return now.getTime() - from > hours * 3600 * 1000;
}
/* ==========================================================================
 * 杂项
 * ========================================================================== */
/** 带类型的 Object.entries */
export function entries(obj) {
    return Object.entries(obj);
}
/** 去掉对象中的 undefined，便于构造可选字段 */
export function compact(obj) {
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
        if (v !== undefined)
            out[k] = v;
    }
    return out;
}
/** 分页参数规范化：限制 pageSize 上限，防止一次拉全表 */
export function normalizePaging(page, pageSize, maxPageSize = 200) {
    const p = Math.max(1, Math.floor(page ?? 1));
    const ps = Math.min(maxPageSize, Math.max(1, Math.floor(pageSize ?? 20)));
    return { page: p, pageSize: ps, offset: (p - 1) * ps };
}
/** 稳定的字符串哈希（用于文件去重、缓存键），FNV-1a */
export function stableHash(input) {
    let hash = 0x811c9dc5;
    for (let i = 0; i < input.length; i++) {
        hash ^= input.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
}
/* ==========================================================================
 * 发票税率展示
 * ========================================================================== */
/** 税率的来源。**必须如实标注**，见下方说明 */
export const TaxRateSource = {
    /** 取自票面明细行的税率 */
    DETAIL: 'DETAIL',
    /** 由「税额 ÷ 不含税金额」推算的有效税率 */
    DERIVED: 'DERIVED',
};
/**
 * 把税率规范成展示文本，如 `13%`、`6.5%`。
 *
 * 通道返回的写法不统一（`13%`、`13`、`0.13`、`免税`、`***`），这里只做
 * **保守的**规整：认得出的百分比转成统一写法，认不出的**原样保留**——
 * 猜一个数字填上去，比显示原始字符串更危险。
 */
function normalizeRateText(raw) {
    const t = raw.trim();
    if (t === '')
        return null;
    // 明确非数值的表述（免税/不征税/***）原样保留，它们本身就是有效信息
    if (!/\d/.test(t))
        return t;
    const num = Number(t.replace(/[%％\s]/g, ''));
    if (!Number.isFinite(num))
        return t;
    // 0.13 这种小数写法按比例还原成 13
    const pct = num > 0 && num <= 1 && !t.includes('%') && !t.includes('％') ? num * 100 : num;
    // 去掉无意义的小数尾零：13.00 → 13
    const text = String(Number(pct.toFixed(4)));
    return `${text}%`;
}
/**
 * 生成发票级税率展示文本。
 *
 * 【为什么需要它】
 * 发票表上**没有**发票级税率——税率只存在于 OCR 明细行里（每行一个），
 * 而导出/列表要的是一个发票级的单元格。这个函数把两者的取舍固定下来，
 * 前后端共用一份规则，避免"页面上是 13%、导出里是 0.13"。
 *
 * 【三条不可让步的规则】
 *  1. **多档税率如实并列**（`13%、9%`），绝不取平均——平均值票面上不存在，
 *     却看起来像一个真实税率，是最容易被当真的假数据。
 *  2. **推算是推算，必须标注**。明细行没有税率时用「税额÷不含税金额」算有效税率，
 *     文本后缀 `（推算）`。不标注就等于把推算值冒充成票面税率。
 *  3. **两者都没有就返回 null**（导出留空），不填 0、不填 "-"。
 *     Excel 里 0 会被求和当真，"-" 会被当字符串——留空才是诚实。
 */
export function resolveInvoiceTaxRateText(input) {
    const normalized = [];
    for (const raw of input.detailRates) {
        if (raw === null || raw === undefined)
            continue;
        const text = normalizeRateText(String(raw));
        if (text !== null && !normalized.includes(text))
            normalized.push(text);
    }
    if (normalized.length > 0) {
        // 多档如实并列；顺序按数值大小更易读，非数值的（免税）排在最后
        normalized.sort((a, b) => {
            const na = Number(a.replace('%', ''));
            const nb = Number(b.replace('%', ''));
            if (Number.isFinite(na) && Number.isFinite(nb))
                return nb - na;
            if (Number.isFinite(na))
                return -1;
            if (Number.isFinite(nb))
                return 1;
            return 0;
        });
        return normalized.join('、');
    }
    const amount = input.amountCents;
    const tax = input.taxCents;
    // 不含税金额为 0 或缺失时无法推算——除法会得到 Infinity/NaN，绝不能让它流出去
    if (amount === null || amount === undefined || tax === null || tax === undefined || amount === 0) {
        return null;
    }
    const pct = (tax / amount) * 100;
    if (!Number.isFinite(pct))
        return null;
    const text = String(Number(pct.toFixed(2)));
    return `${text}%（推算）`;
}
/* ==========================================================================
 * 税号
 * ========================================================================== */
/**
 * 从任意文本里抠出税号样式的串（15/17/18/20 位大写字母数字混合）。
 *
 * 【为什么是宽松的】它面对的是 OCR 出来的脏文本，目标是"尽可能别漏"。
 * 判定它合不合法是另一个问题，见下面的 `isValidUnifiedSocialCreditCode`。
 *
 * 【为什么放在 shared】发票（`pdfTextOcr`）与合同（`prefill`）都要用。
 * 各写一份迟早会漂——而漂的后果是同一个税号在两边一个有值一个没有。
 */
export function parseTaxNumber(text) {
    const cleaned = text.replace(/[\s\u00a0]/g, '').toUpperCase();
    const m = /([0-9A-Z]{15,20})/.exec(cleaned);
    if (!m)
        return null;
    const v = m[1];
    // 纯数字且长度不足 15 的不算税号（避免把金额当税号）
    return v.length >= 15 ? v : null;
}
/**
 * 统一社会信用代码是否合法（18 位）。
 *
 * 【为什么必须严】合同正文里到处是字母数字混排（合同编号、账号、规格型号），
 * 宽松匹配很容易把「HT2026GJ0001」这种当成税号填进台账——而税号填错会让
 * 供应商主体校验查无此企业，属于"错的值比空值更危险"。
 *
 * 规则：18 位，字符集为 **数字 + 大写字母，但排除 I、O、S、V、Z**
 * （这五个官方规定不使用，避免与 1/0/5/U/2 混淆）。
 * 旧版 15 位纯数字税号同样放行。
 */
export function isValidUnifiedSocialCreditCode(input) {
    const s = input.replace(/[\s\u00a0]/g, '').toUpperCase();
    if (/^\d{15}$/.test(s))
        return true; // 旧版三证合一前的税号
    if (s.length !== 18)
        return false;
    return /^[0-9A-HJ-NPQRTUWXY]{18}$/.test(s);
}
//# sourceMappingURL=utils.js.map